import { Icon } from '@dynamic/ui';
import styled from 'styled-components';

import { TOP_MENU_HEIGHT_PX } from '#/components/Header/Menu.utils';
import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { remify } from '#/service/utils';

const DOT_SIZE = remify(24);

// main container of the whole step indicator
export const StepIndicatorContainer = styled.ol.attrs({
  'data-testid': 'step-indicator',
})`
  position: fixed;
  top: 0;
  display: flex;
  margin-inline: auto;
  gap: ${remify(32)};
  justify-content: center;
  height: calc(${remify(TOP_MENU_HEIGHT_PX)} + var(--bannerHeight, 0rem));
  z-index: 101;
  left: 50%;
  transform: translateX(-50%);

  @media ${MEDIA_QUERIES.lg} {
    padding: 0 ${remify(16)};
    left: initial;
    transform: initial;
  }
`;

StepIndicatorContainer.displayName = 'StepIndicatorContainer';

// container for one step, including the number, the lines, and the title
export const StepContainer = styled.li.attrs({
  'data-testid': 'step-container',
})`
  list-style: none;
  text-align: center;
  font: var(--fontStyleCaption1);
  display: flex;
  align-items: center;
  gap: ${remify(8)};
`;

StepContainer.displayName = 'StepContainer';

// container for the top row of the step, including the number and the lines
export const StepTop = styled.div`
  display: flex;
  justify-content: center;
`;

StepTop.displayName = 'StepTop';

interface StepNumberProps {
  isCurrent?: boolean;
  isDone?: boolean;
}

// step number in a circle
export const StepNumber = styled.div.attrs({
  'data-testid': 'step-number',
})<StepNumberProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${DOT_SIZE};
  height: ${DOT_SIZE};
  z-index: 1;
  border-radius: 100rem;
  background: var(--pageSecondaryBackground);
  color: var(--pageParagraphForeground);

  ${({ isCurrent, isDone }) =>
    isCurrent || isDone
      ? `stroke: var(--itemFocusFrame)};
    stroke-width: 1px;
    outline: 1px solid var(--pageParagraphForeground);`
      : 'opacity: 0.5;'}
`;

StepNumber.displayName = 'StepNumber';

// title of the step
export const StepTitle = styled.div.attrs({
  'data-testid': 'step-title',
})<{ $isCurrent?: boolean }>`
  grid-area: title;
  padding-inline: 0.1rem;
  opacity: ${({ $isCurrent }) => ($isCurrent ? 1 : 0.5)};

  @media ${MEDIA_QUERIES.xs} {
    font: var(--fontStyleCaption2);
  }

  @media ${MEDIA_QUERIES.lg} {
    display: none;
  }
`;

StepTitle.displayName = 'StepTitle';

// checkmark icon for steps that are done
export const StepDoneIcon = styled(Icon).attrs({
  name: 'checkmark',
  'data-testid': 'step-done-icon',
})`
  width: 1rem;
  height: 1rem;
`;
